import React, { useState } from 'react';
import styled from 'styled-components';
import { Box, Divider, Typography } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useTranslation } from 'react-i18next';

import { DetailBox } from 'common/components/DetailBox';
import { useCompanyQuery } from 'graphql/types';
import { Link } from 'common/components/Link';
import { UserInfoSection } from 'user/components/UserInfoSection';
import { useSnackbarContext } from 'common/context';
import { ButtonLink } from 'common/components/buttons/ButtonLink';
import { UserForm } from 'user/components/forms/UserForm';
import { ISnackbarState } from 'common/interfaces/snackbarState';

interface IProps {
    className?: string;
}

export const BaseCompanyUsersDetailBox = ({ className }: IProps) => {
    const { t } = useTranslation();
    const { setSnackbarState } = useSnackbarContext();
    const [isEditing, setIsEditing] = useState(false);
    const {
        data,
        loading,
        error,
        refetch: refetchCompany,
    } = useCompanyQuery({
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
    });
    const { company } = data || {};

    const handleClose = (snackbarState: ISnackbarState) => {
        if (snackbarState) {
            setSnackbarState(snackbarState);

            if (snackbarState.variant === 'error') return;
        }

        setIsEditing(false);
    };

    const handleDeleteUser = (snackbarState: ISnackbarState) => {
        setSnackbarState(snackbarState);

        refetchCompany();
    };

    const userList = (
        <>
            {company?.users.length ? (
                company?.users.map((user, index) => (
                    <UserInfoSection
                        divider={index !== company.users.length - 1}
                        key={index}
                        user={user}
                        onDeleteUser={handleDeleteUser}
                    />
                ))
            ) : (
                <Box textAlign="center">
                    <Typography color="textSecondary">
                        {t('company.emptyUserListMessage')}
                    </Typography>
                </Box>
            )}

            <Box mb={8} mt={8}>
                <Divider />
            </Box>
        </>
    );

    const content = isEditing ? (
        <>
            {userList}

            <UserForm onClose={handleClose} />
        </>
    ) : (
        <>
            {userList}

            <Box display="flex" flexDirection="column">
                <ButtonLink
                    icon={<ArrowForwardIcon />}
                    onClick={() => setIsEditing(true)}
                >
                    {t('company.addNewUser')}
                </ButtonLink>
            </Box>
        </>
    );

    return (
        <DetailBox
            className={className}
            error={!!error}
            id="administrators"
            loading={loading}
            title={t('company.usersHeader')}
        >
            {content}
        </DetailBox>
    );
};

export const CompanyUsersDetailBox = styled(BaseCompanyUsersDetailBox)`
    ${Link} {
        font-weight: 700;
    }
`;
